<script setup lang="ts">
import type { News } from '@xianlaibang/shared/types'
import { newsApi } from '@/api' 

// --- 新闻资讯 ---

// 新闻类别
enum NewsCategory {
  NEW = '7',
  COST = '4',
  RECYCLE = '8'
}

// ==========================================
// 获取全部新闻
// ==========================================
const allNews = ref<News[]>()
const allTotal = ref(0)
async function getAllNews() {
  try {
    const { rows, total } = await newsApi.getNews({ pageNum: 1, pageSize: 4 })
    allNews.value = rows
    allTotal.value = total || 0
  } catch (error) {
  }
}

// ==========================================
// 获取新手攻略
// ==========================================
const newNews = ref<News[]>()
const newTotal = ref(0)
async function getNewNews() {
  try {
    const { rows, total } = await newsApi.getNews({ pageNum: 1, pageSize: 5, contentCategoryId: NewsCategory.NEW })
    newNews.value = rows
    newTotal.value = total || 0
  } catch (error) {
  }
}

// ==========================================
// 获取话费接单
// ==========================================
const costNews = ref<News[]>()
const costTotal = ref(0)
async function getCostNews() {
  try {
    const { rows, total } = await newsApi.getNews({ pageNum: 1, pageSize: 5, contentCategoryId: NewsCategory.COST })
    costNews.value = rows
    costTotal.value = total || 0
  } catch (error) {
  }
}

// ==========================================
// 获取卡券回收
// ==========================================
const recycleNews = ref<News[]>()
const recycleTotal = ref(0)
async function getRecycleNews() {
  try {
    const { rows, total } = await newsApi.getNews({ pageNum: 1, pageSize: 5, contentCategoryId: NewsCategory.RECYCLE })
    recycleNews.value = rows
    recycleTotal.value = total || 0
  } catch (error) {
  }
}

async function getNewsList() {
  // TODO: 获取新闻列表:包括所有资讯/新手攻略/卡券回收/话费接单的列表
  try {
    await getAllNews()
    await getNewNews()
    await getCostNews()
    await getRecycleNews()
  } catch (error) {
  }
}

// 获取资讯概要
function getNewsSummary(news: News) {
  // 去除html标签
  const newsContent = (news.newsContent || '').replace(/<[^>]+>/g, '')
  return newsContent.length > 70 ? `${newsContent.slice(0, 70)}...` : newsContent
}

function formatNewsDate(date?: string) {
  return date ? date.slice(0, 10) : ''
}

const displayNews = computed(() => {
  const newsMap = new Map<number, News>()

  ;[
    ...(allNews.value || []),
    ...(newNews.value || []),
    ...(costNews.value || []),
    ...(recycleNews.value || [])
  ].forEach((item) => {
    if (item?.platformNewsId && !newsMap.has(item.platformNewsId)) {
      newsMap.set(item.platformNewsId, item)
    }
  })

  return Array.from(newsMap.values()).slice(0, 10)
})

const newsColumns = computed(() => {
  const list = displayNews.value
  return [
    list.slice(0, 5),
    list.slice(5, 10)
  ]
})

const loading = ref(false)
onMounted(async () => {
  loading.value = true
  await getNewsList()
  loading.value = false
})

</script>
<template>
  <div v-loading="loading" class="news-section">
    <div class="news-list">
      <div
        v-for="(column, columnIndex) in newsColumns"
        :key="columnIndex"
        class="news-list__column"
      >
        <NuxtLink
          v-for="news in column"
          :key="`${columnIndex}-${news.platformNewsId}`"
          :to="`/news/detail/${news.platformNewsId}`"
          class="news-list__item"
        >
          <h3 class="news-list__title">{{ news.newsTitle }}</h3>
          <p class="news-list__summary">{{ getNewsSummary(news) }}</p>
          <div class="news-list__meta">
            <span>{{ formatNewsDate(news.createTime) }}</span>
            <span class="news-list__views">
              <ElIcon><ElIconView /></ElIcon>
              {{ news.browseCount || 0 }}
            </span>
          </div>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
/* 6. 新闻资讯 */
.news-section {
  background: #fff;
}

.news-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 84px;
  row-gap: 0;
  padding: 0 8px;

  &__column {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__item {
    display: block;
    color: inherit;
    text-decoration: none;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateX(3px);

      .news-list__title {
        color: #ff7440;
      }
    }
  }

  &__title {
    margin: 0 0 5px;
    color: #2c3b4c;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.32;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  &__summary {
    display: -webkit-box;
    min-height: 38px;
    margin: 0;
    overflow: hidden;
    color: #aebccc;
    font-size: 13px;
    line-height: 1.45;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 3px;
    color: #b0bdca;
    font-size: 12px;
  }

  &__views {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #7892ad;
    font-weight: 600;

    .el-icon {
      font-size: 14px;
    }
  }
}

@media (max-width: 1200px) {
  .news-list {
    column-gap: 40px;
  }
}

@media (max-width: 768px) {
  .news-list {
    grid-template-columns: 1fr;
    gap: 24px;

    &__column {
      gap: 24px;
    }
  }
}
</style>
